import { useEffect, useRef, useState } from 'react';
/** Keep the phone control in its video stage, independent of chat docking. */
export default function usePhoneNumpadPosition(compact) {
    const panel = useRef(null);
    const requested = useRef(null);
    const drag = useRef(null);
    const update = useRef(() => { });
    const [position, setPosition] = useState(null);
    useEffect(() => {
        const element = panel.current;
        const stage = element?.parentElement;
        if (!element || !stage)
            return;
        const place = () => {
            const bounds = stage.getBoundingClientRect();
            const box = element.getBoundingClientRect();
            const maxX = Math.max(8, bounds.width - box.width - 8);
            const maxY = Math.max(56, bounds.height - box.height - 88);
            let x = !compact && requested.current ? requested.current.x * maxX : maxX;
            let y = !compact && requested.current ? requested.current.y * maxY : (bounds.height - box.height) / 2;
            x = Math.max(8, Math.min(maxX, x));
            y = Math.max(56, Math.min(maxY, y));
            const self = stage.querySelector('[data-self-view="true"]')?.getBoundingClientRect();
            // On short landscape screens even the middle can touch a corner
            // tile. Move the compact opener across; leave a dragged open panel
            // under the user's control.
            if (compact && self && x + bounds.left < self.right + 8 && x + bounds.left + box.width > self.left - 8
                && y + bounds.top < self.bottom + 8 && y + bounds.top + box.height > self.top - 8) {
                x = self.left + self.width / 2 > bounds.left + bounds.width / 2 ? 8 : maxX;
            }
            setPosition(old => old?.x === x && old.y === y ? old : { x, y });
        };
        update.current = place;
        const resize = new ResizeObserver(place);
        resize.observe(stage);
        resize.observe(element);
        const changes = new MutationObserver(records => {
            if (records.some(record => record.type === 'childList'
                || record.target instanceof Element && record.target.matches('[data-self-view]')))
                place();
        });
        changes.observe(stage, { subtree: true, childList: true, attributes: true, attributeFilter: ['style', 'data-self-view'] });
        place();
        return () => { resize.disconnect(); changes.disconnect(); update.current = () => { }; };
    }, [compact]);
    const moveTo = (x, y) => {
        const element = panel.current;
        const stage = element?.parentElement?.getBoundingClientRect();
        if (!element || !stage)
            return;
        const box = element.getBoundingClientRect();
        const maxX = Math.max(8, stage.width - box.width - 8);
        const maxY = Math.max(56, stage.height - box.height - 88);
        requested.current = { x: Math.max(8, Math.min(maxX, x)) / maxX, y: Math.max(56, Math.min(maxY, y)) / maxY };
        update.current();
    };
    const handle = {
        'aria-label': 'Move dialpad. Drag or use arrow keys.',
        onPointerDown: (event) => {
            const box = panel.current?.getBoundingClientRect();
            if (!box || !event.isPrimary || event.button !== 0)
                return;
            event.preventDefault();
            drag.current = { id: event.pointerId, x: event.clientX - box.left, y: event.clientY - box.top };
            event.currentTarget.setPointerCapture(event.pointerId);
        },
        onPointerMove: (event) => {
            const bounds = panel.current?.parentElement?.getBoundingClientRect();
            if (!bounds || drag.current?.id !== event.pointerId)
                return;
            moveTo(event.clientX - bounds.left - drag.current.x, event.clientY - bounds.top - drag.current.y);
        },
        onPointerUp: () => { drag.current = null; },
        onPointerCancel: () => { drag.current = null; },
        onLostPointerCapture: () => { drag.current = null; },
        onKeyDown: (event) => {
            if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key))
                return;
            event.preventDefault();
            event.stopPropagation();
            const box = panel.current?.getBoundingClientRect();
            const bounds = panel.current?.parentElement?.getBoundingClientRect();
            if (box && bounds)
                moveTo(box.left - bounds.left + (event.key === 'ArrowLeft' ? -24 : event.key === 'ArrowRight' ? 24 : 0), box.top - bounds.top + (event.key === 'ArrowUp' ? -24 : event.key === 'ArrowDown' ? 24 : 0));
        }
    };
    const style = position ? { left: position.x, top: position.y } : { right: 8, top: '50%', transform: 'translateY(-50%)' };
    return { panel, style, handle };
}
